import { IMaskModule } from 'angular-imask';
import { NgxPermissionsModule } from 'ngx-permissions';
import { IonicModule } from '@ionic/angular';

import { MatPasswordStrengthModule } from '@angular-material-extensions/password-strength';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppConfigService } from './config/app.config';
import { Environment } from './config/environment';
import { MaintenanceComponent } from './modules/shared/maintenance/maintenance.component';
import { MaterialModule } from './modules/shared/material/material.module';
import { SharedModule } from './modules/shared/shared.module';
import { SpinnerHideComponent } from './modules/shared/spinner-hide/spinner.component';
import { SpinnerComponent } from './modules/shared/spinner/spinner.component';
import { TutorialModule } from './modules/tutorial/tutorial.module';
import { ImmunizationComponent } from './modules/wellness/immunization/immunization.component';
import { VaxPassCardComponent } from './modules/wellness/vax-pass/vax-pass-card/vax-pass-card.component';
import { VaxPassComponent } from './modules/wellness/vax-pass/vax-pass.component';
import { VaxStatusComponent } from './modules/wellness/vax-pass/vax-status/vax-status.component';
import { VaxpassConsentFormComponent } from './modules/wellness/vax-pass/vaxpass-consent-form/vaxpass-consent-form.component';
import { AuthGuard } from './services/core/auth-guard.service';
import { AuthService } from './services/core/auth.service';
import { FeatureFlagGuardService } from './services/core/feature-flag-guard.service';
import { ProfileService } from './services/core/profile.service';
import { SimplifiedSchedulingGuard } from './services/core/simplified-scheduling.guard.service';
import { TitleService } from './services/core/title.service';
import { FindAppointmentService } from './services/scheduling/find-appointment/find-appointment.service';
import { ActivateAccountComponent } from './views/activate-account/activate-account.component';
import { BaseForgotPasswordComponent } from './views/base-forgot-password/base-forgot-password.component';
import { BaseResetPasswordComponent } from './views/base-reset-password/base-reset-password.component';
import { ForgotPasswordComponent } from './views/forgot-password/forgot-password.component';
import { HomeComponent } from './views/home/home.component';
import { LoginScreenVariationComponent } from './views/login-screen-variation/login-screen-variation.component';
import { LoginSimplifiedComponent } from './views/login-simplified/login-simplified.component';
import { LoginComponent } from './views/login/login.component';
import { MigrationModule } from './views/migration/migration.module';
import { ResetPasswordComponent } from './views/reset-password/reset-password.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { SimplifiedFindAppointmentService } from './services/scheduling/simplified-find-appointment/simplified-find-appointment.service';
import { Capacitor } from '@capacitor/core';
import { datadogRum } from '@datadog/browser-rum';

const appInitializerFn = (appConfig: AppConfigService) => {
	return () => {
		return appConfig.load();
	};
};

const translateInitializerFn = (translate: TranslateService) => {
	return async () => {
		const availableLanguages = ['en', 'es'];
		const browserLang = navigator.language?.split('-')[0] || 'en';
		const lang = availableLanguages.includes(browserLang) ? browserLang : 'en';
		translate.setDefaultLang('en');

		const logFailure = (failedLang: string, role: string, err: unknown) => {
			try {
				datadogRum.addAction('i18n_load_failure', {
					lang: failedLang,
					role,
					effectiveType: (navigator as any).connection?.effectiveType,
					message: err instanceof Error ? err.message : String(err),
				});
			} catch {
				/* RUM not yet initialized — actions buffer until init() or drop silently */
			}
			console.error(`i18n: failed to load ${failedLang} (${role})`, err);
		};

		// ngx-translate's use() can resolve successfully with empty translations
		// when the loader fails, so fetch + verify + set explicitly for control.
		const tryLoad = async (langToLoad: string, role: string): Promise<boolean> => {
			try {
				const translations = await translate.getTranslation(langToLoad).toPromise();
				if (!translations || Object.keys(translations).length === 0) {
					throw new Error('empty translations');
				}
				translate.setTranslation(langToLoad, translations);
				return true;
			} catch (err) {
				logFailure(langToLoad, role, err);
				return false;
			}
		};

		// Always preload English so a fallback exists even when the primary
		// language fetch (e.g. es.json) returns empty or fails.
		const englishLoaded = await tryLoad('en', 'preload');
		const primaryLoaded = lang === 'en' ? englishLoaded : await tryLoad(lang, 'primary');

		// Always activate a real language. Falling back to English keeps
		// setDefaultLang consistent and gives ngx-translate's loader a retry
		// path if English failed transiently above.
		const activeLang = primaryLoaded ? lang : 'en';

		try {
			await translate.use(activeLang).toPromise();
		} catch (err) {
			logFailure(activeLang, 'use', err);
		}
	};
};

@NgModule({
	declarations: [
		AppComponent,
		LoginComponent,
		LoginSimplifiedComponent,
		ForgotPasswordComponent,
		ResetPasswordComponent,
		MaintenanceComponent,
		HomeComponent,
		BaseForgotPasswordComponent,
		BaseResetPasswordComponent,
		ActivateAccountComponent,
		VaxStatusComponent,
		VaxPassComponent,
		VaxpassConsentFormComponent,
		VaxPassCardComponent,
		ImmunizationComponent,
		LoginScreenVariationComponent,
	],
	imports: [
		IonicModule.forRoot({
			animated: Capacitor.isNativePlatform(),
		}),
		BrowserModule,
		AppRoutingModule,
		SharedModule,
		NoopAnimationsModule,
		ReactiveFormsModule,
		MaterialModule,
		IMaskModule,
		HttpClientModule,
		BrowserAnimationsModule,
		NgxPermissionsModule.forRoot(), // load permissions module globally to work
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient],
			},
		}),
		TutorialModule,
		NoopAnimationsModule,
		MigrationModule,
		MatPasswordStrengthModule.forRoot(),
		MatTabsModule,
	],
	providers: [
		TitleService,
		AuthService,
		AuthGuard,
		FeatureFlagGuardService,
		AppConfigService,
		ProfileService,
		Environment,
		{
			provide: APP_INITIALIZER,
			useFactory: appInitializerFn,
			deps: [AppConfigService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: translateInitializerFn,
			deps: [TranslateService],
			multi: true,
		},
		SpinnerComponent,
		SpinnerHideComponent,
		{ provide: LocationStrategy, useClass: PathLocationStrategy },
		SimplifiedSchedulingGuard,
		{ provide: FindAppointmentService, useClass: FindAppointmentService },
		{ provide: SimplifiedFindAppointmentService, useClass: SimplifiedFindAppointmentService },
		{
			provide: MAT_SELECT_CONFIG,
			useValue: { panelWidth: null },
		},
	],
	bootstrap: [AppComponent],
})
export class AppModule {
	constructor(titleService: TitleService) {
		titleService.init();
	}
}
// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
	return new TranslateHttpLoader(http, './assets/i18n/', '.json?v=16.0.0');
}
